<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="ml-1"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="cardProfiles"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="manageCardAccountList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(requestDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(issuedDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(expiryDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(isATMEnabled)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value ? 'Yes' : 'No')"
        >
          {{ data.value ? $t('Yes') : $t('No') }}
        </b-badge>
      </template>
      <template #cell(isPOSEnabled)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value ? 'Yes' : 'No')"
        >
          {{ data.value ? $t('Yes') : $t('No') }}
        </b-badge>
      </template>
      <template #cell(isWebEnabled)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value ? 'Yes' : 'No')"
        >
          {{ data.value ? $t('Yes') : $t('No') }}
        </b-badge>
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(issuanceStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ addSpaceToString(data.value) }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div
          v-if="data.item.status !== 'Hotlisted'"
          class="text-nowrap"
        >
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="data.item.requestType === 'Unassigned' && getUserPermission('WITHDRAW_CARD')"
              @click="onWithdrawCard(data.item)"
            >
              <feather-icon icon="MinusSquareIcon" />
              <span class="align-middle ml-50"> {{ $t('Withdraw Card') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.status === 'Generated' && data.item.issuanceStatus !== 'NotIssued' && getUserPermission('ACTIVATE_CARD')"
              @click="onActivateCard(data.item)"
            >
              <feather-icon icon="MinusSquareIcon" />
              <span class="align-middle ml-50"> {{ $t('Activate Card') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.status === 'Active' && getUserPermission('HOTLIST_CARD')"
              @click="onHotListCard(data.item)"
            >
              <feather-icon icon="ListIcon" />
              <span class="align-middle ml-50"> {{ $t('Hotlist Card') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'BLOCK_CARD' : '')"
              @click="cardAccountStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Block Card') : '' }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Blocked' ? 'UNBLOCK_CARD' : '')"
              @click="cardAccountStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Blocked' ? $t('Unblock Card') : '' }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCardAccounts"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Number-->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter Account Number')"
            type="text"
          />
        </b-col>
        <!-- Account Number-->
        <!-- Masked PAN-->
        <b-col md="6">
          <InputField
            id="masked-pan"
            v-model="filter.maskedPan"
            :label="$t('Masked PAN')"
            :placeholder="$t('Enter Masked PAN')"
            type="text"
          />
        </b-col>
        <!-- Masked PAN-->

        <!--Batch Request ID -->
        <b-col md="6">
          <InputField
            id="batch-no"
            v-model="filter.batchNo"
            :label="$t('Batch Number')"
            :placeholder="$t('Enter Batch Number')"
            type="text"
          />
        </b-col>
        <!--Batch Request ID -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Card Status')"
            :placeholder="$t('Select')"
            :options="translatedCardStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
        <b-col md="6">
          <DatePickerField
            id="card-expiry"
            v-model="filter.cardExpiry"
            :min-date="new Date()"
            :label="$t('Card Expiry')"
            :config="datePickerConfig"
            :placeholder="$t('Card Expiry')"
          />
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BBadge, BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import { required, minPhoneVal, maxPhoneVal } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { gender, cardStatusOptions } from '../../../../../helpers/data'
import getStatusColor from '../../../../../helpers/getStatusColor'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import Button from '../../../../components/button/Button.vue'
// import { checkTokenValidity } from '@/helpers/token'
import addSpaceToString from '../../../../../helpers/addSpaceToString'
import { toDateWithYear } from '../../../../../helpers/formatDate'
import { eChannelsPermission } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BBadge,
    TablePagination,
    BModal,
    BSpinner,
    TableEntries,
    SelectField,
    InputField,
    Button,
    DatePickerField,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      addSpaceToString,
      toDateWithYear,
      isReadonly: false,
      isEditable: false,
      isFullName: false,
      showSpinner: false,
      sortBy: 'name',
      required,
      minPhoneVal,
      maxPhoneVal,
      // cardStatusOptions,
      modalTitle: this.$t('Card Profile Setup'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'batchNo', sortable: true, label: 'Batch Number' },
        { key: 'maskedPan', sortable: true, label: 'Masked PAN' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'nameOnCard', sortable: true, label: 'Account Name' },
        { key: 'requestDate', sortable: true, label: 'Card Request Date' },
        { key: 'issuedDate', sortable: true, label: 'Card Issued Date' },
        { key: 'expiryDate', sortable: true, label: 'Card Expiry Date' },
        { key: 'isATMEnabled', sortable: true, label: 'ATM Enabled' },
        { key: 'isPOSEnabled', sortable: true, label: 'POS Enabled' },
        { key: 'isWebEnabled', sortable: true, label: 'WEB Enabled' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'issuanceStatus', sortable: true, label: 'Issuance Status' },
        { key: 'actions', label: 'Actions' },
      ],
      genderOptions: gender,
      getStatusColor,
      buttonLabel: this.$t('Save'),
      manageCardAccountList: [],
      filter: {
        accountNumber: '',
        maskedPan: '',
        batchNo: '',
        status: '',
        cardExpiry: null,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        defaultDate: '',
      },
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedCardStatusOptions() {
      return cardStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCardAccounts()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCardAccounts()
    },
  },
  mounted() {
    this.fetchCardAccounts()
  },
  methods: {
    ...mapActions({
      getManageCardList: 'eChannels/getManageCardList',
      updateCardAccountStatus: 'eChannels/updateCardStatus',
      withdrawCardAction: 'eChannels/withdrawCardAction',
      downloadCardsReport: 'reports/downloadCardsReport',
    }),
    async fetchCardAccounts() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getManageCardList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.MANAGE_CARDS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.manageCardAccountList = data.data
          this.totalCount = data.recordCount
        } else {
          this.manageCardAccountList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onWithdrawCard(item) {
      const formData = {
        id: item.id,
      }
      try {
        this.isLoading = true
        this.showSpinner = true
        const { status } = await this.withdrawCardAction({
          formData,
          permission: eChannelsPermission.WITHDRAW_CARD,

        })
        if (status === 200) {
          this.alertSuccess({ text: this.$t('Your request to withdraw card was successful') })
          this.fetchCardAccounts()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.showSpinner = false
      }
    },
    onHotListCard(item) {
      const performHotListAction = async () => {
        try {
          const { id } = item
          const statusType = 3
          this.isLoading = true
          this.showSpinner = true
          const statusValues = {
            id,
            status: statusType,
          }
          const { status } = await this.updateCardAccountStatus({
            statusValues,
            permission: eChannelsPermission.HOTLIST_CARD,
          })
          if (status === 200) {
            this.alertSuccess({ text: this.$t('Your request to Hotlist this Card was successful') })
            this.fetchCardAccounts()
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.isLoading = false
          this.showSpinner = false
        }
      }
      this.confirm(performHotListAction, { title: '', text: this.$t('Are you sure you want to Hotlist this Card?') })
    },
    onActivateCard(item) {
      const activateCardAction = async () => {
        try {
          const { id } = item
          const statusType = 4
          this.isLoading = true
          this.showSpinner = true
          const statusValues = {
            id,
            status: statusType,
          }
          const { status } = await this.updateCardAccountStatus({
            statusValues,
            permission: eChannelsPermission.ACTIVATE_CARD,
          })
          if (status === 200) {
            this.alertSuccess({ text: this.$t('Your request to Activate this Card was successful') })
            this.fetchCardAccounts()
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.isLoading = false
          this.showSpinner = false
        }
      }
      this.confirm(activateCardAction, { title: '', text: this.$t('Are you sure you want to Activate this Card?') })
    },
    cardAccountStatus(items) {
      const { id } = items
      const action = items.status === 'Active' ? this.$t('Block') : this.$t('Unblock')
      const statusType = items.status === 'Active' ? 1 : 2
      const permission = items.status === 'Active' ? eChannelsPermission.BLOCK_CARD : eChannelsPermission.UNBLOCK_CARD

      const updateCardStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            status: statusType,
          }
          const response = await this.updateCardAccountStatus({
            statusValues, permission,
          })
          if (response.status === 200) {
            this.fetchCardAccounts()
            const requestText = this.$t('Your request to')
            const thisText = this.$t('this Card was successful')
            this.alertSuccess({ text: `${requestText} ${action} ${thisText}` })
            this.showSpinner = false
          }
        } catch (error) {
          error.response.data.errors.forEach(responseError => {
            const requestText = this.$t('Your request to')
            const text1 = this.$t('this Card failed. Reason')
            const errorMessage = `${requestText} ${action} ${text1}: ${responseError.errorMessage}`
            this.alertError({ text: errorMessage })
          })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const text2 = this.$t('this Card?')
      this.confirm(updateCardStatus, { title: '', text: `${areYouSure} ${action} ${text2}` })
    },

    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCardsReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: eChannelsPermission.MANAGE_CARDS,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCardsReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: eChannelsPermission.MANAGE_CARDS,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCardAccounts()
    },
  },
}
</script>
